// frontend/src/components/promotions/PromotionMembers.jsx
//
// The detail row of the promotions list: the items a promotion covers, read
// when the row opens. An 'all items' promotion keeps no item rows — it covers
// the whole store — so it says that instead.
//
// The detail shows the first page of the items endpoint; when the promotion
// has more, the line under it says how many it shows and where to see them all.
// An item its price push left at the regular price says why
// (`not_started_reason`, PromotionItem.NOT_STARTED_*), in a column that shows
// only while one does.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import DetailGrid from '../grid/DetailGrid';
import DetailRow from '../grid/DetailRow';
import { useIsShopify, itemIdentityColumns } from '../grid/productIdentity';
import { useFormatters } from '../../hooks/useFormatters';
import { usePromotionMembers } from '../../hooks/usePromotions';

const NOTE_SX = { mt: 1, fontSize: 12, color: 'text.secondary' };

function MemberGrid({ promotionId }) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const { formatNumber } = useFormatters();
  const { members, loading, error } = usePromotionMembers(promotionId);
  const anyNotStarted = Boolean(members?.results.some((row) => row.not_started_reason));

  const columnDefs = useMemo(() => {
    const notStarted = {
      price_not_below: t('promotions.notStarted.price_not_below'),
      no_price: t('promotions.notStarted.no_price'),
      not_linked: t('promotions.notStarted.not_linked'),
      store_refused: t('promotions.notStarted.store_refused'),
      internal: t('promotions.notStarted.internal'),
    };
    return [
    ...itemIdentityColumns(isShopify, {
      productHeader: t('promotions.grid.product'),
      codeHeader: t('promotions.grid.itemCode'),
      nameHeader: t('promotions.grid.itemName'),
    }),
    {
      field: 'current_sell_price',
      headerName: t('promotions.grid.currentPrice'),
      type: 'decimalColumn',
    },
    {
      field: 'promotional_sell_price',
      headerName: t('promotions.grid.promoPrice'),
      type: 'decimalColumn',
    },
    ...(anyNotStarted ? [{
      field: 'not_started_reason',
      headerName: t('promotions.grid.notOnSale'),
      flex: 2,
      minWidth: 220,
      valueFormatter: (params) => (params.value ? notStarted[params.value] : ''),
    }] : []),
    ];
  }, [t, isShopify, anyNotStarted]);

  if (error) {
    return (
      <DetailRow>
        <Box sx={{ fontSize: 12, color: 'error.main' }}>{error}</Box>
      </DetailRow>
    );
  }

  return (
    <DetailGrid
      rowData={members?.results}
      loading={loading}
      columnDefs={columnDefs}
      stateKey="promotions_detail"
      footer={members && members.count > members.results.length ? (
        <Box sx={NOTE_SX}>
          {t('promotions.membersShown', {
            shown: formatNumber(members.results.length, 0),
            count: formatNumber(members.count, 0),
          })}
        </Box>
      ) : null}
    />
  );
}

export default function PromotionMembers({ data }) {
  const { t } = useTranslation();
  if (data.item_selection_mode === 'all') {
    return (
      <DetailRow>
        <Box sx={{ ...NOTE_SX, mt: 0 }}>{t('promotions.allItemsInfo')}</Box>
      </DetailRow>
    );
  }
  return <MemberGrid promotionId={data.PromotionID} />;
}
